import { Injectable } from '@angular/core';

import { FixtureState, OfferMapping } from '@cds/query-objects';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { BetslipUpsellConfig } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { Observable, filter, map } from 'rxjs';

import { BetslipUpsellApi } from '../../../../lib/cds/betslip-upsell-api.service';
import { DateCheckerService } from '../../../../lib/common/date-checker.service';
import { IBetslipState } from '../../../base/store/state';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../../core/picks/betslip-v2-pick';
import { PickOddsState } from '../../../core/picks/pick-models';
import { isSportPick } from '../../picks/services/betslip-pick-helpers';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import { PickGridGroupSport, UpsellData } from '../models';

@Injectable()
export class UpsellDataService {
    constructor(
        private betslipUpsellConfig: BetslipUpsellConfig,
        private fixtureFactory: FixtureFactory,
        private betslipUpsellApi: BetslipUpsellApi,
        private betslipPickUtils: BetslipPickUtilsService,
    ) {}

    getUpsellEventData$(picks: PickGridGroupSport[]): Observable<UpsellData> {
        const { from, to } = this.getDateRange(picks);

        return this.betslipUpsellApi
            .getBetslipUpsellData({
                state: FixtureState.PreMatch,
                offerMapping: OfferMapping.Filtered,
                sportIds: picks[0].sportId.toString(),
                gridGroupIds: picks[0].gridGroupId,
                take: this.betslipUpsellConfig.maxUpsells,
                from,
                to,
                restrictedFixtureIds: picks.map((pick) => pick.fixtureId).join(','),
            })
            .pipe(
                filter(hasValue),
                map((betslipUpsellFixtures) => {
                    const events = betslipUpsellFixtures.upsellFixtures
                        .map((upsellData) => ({ event: this.fixtureFactory.create(upsellData.fixture), upsellData }))
                        .filter((f) => f.event.optionGroups?.length);

                    return {
                        events,
                        gridGroupId: picks[0].gridGroupId,
                    };
                }),
            );
    }

    getPickDetails(state: IBetslipState): PickGridGroupSport[] {
        const pickList = state.picks.pickList;
        if (pickList.some((pick) => BetslipBetBuilderPick.isPick(pick))) {
            return [];
        }
        const selectedPicksIds = this.betslipPickUtils.getSelectedPicks(state.types, pickList).map((pick) => pick.id);

        const upsellPicks = pickList
            .filter((pick) => pick.oddsState === PickOddsState.Open && selectedPicksIds.includes(pick.id))
            .flatMap((betslipPick) => (BetslipGroupPick.isPick(betslipPick) ? betslipPick.getLegs() : betslipPick))
            .filter((p) => isSportPick(p));

        if (!upsellPicks.length || upsellPicks.some((pick) => pick.isLive)) {
            return [];
        }

        const picks = upsellPicks.map((pick) => {
            const eventDate = BetslipV2Pick.isPick(pick) ? pick.fixture.eventDate : (pick as BetslipV1Pick).event.eventDate;
            const gridGroupId = BetslipV2Pick.isPick(pick) ? pick.market.gridGroups?.[0] : (pick as BetslipV1Pick).market.gridGroups?.[0];

            return {
                sportId: pick.sportId,
                gridGroupId: gridGroupId ?? undefined,
                startsAt: eventDate.toISOString(),
                isLive: pick.isLive,
                fixtureId: pick.id.fixtureId,
                isLocked: false,
                sourceOfPick: pick.tracking.source,
            };
        });

        return picks;
    }

    private getDateRange(picks: PickGridGroupSport[]): { from: string; to: string } {
        const eventDates = picks.map((pick) => new Date(pick.startsAt));
        const minDate = new Date(Math.max.apply(null, eventDates));
        const maxDate = new Date(Math.min.apply(null, eventDates));
        const fromDate = DateProviderService.subtractHours(maxDate, this.betslipUpsellConfig.timeRangeDifferenceValue);

        return {
            from: DateCheckerService.isDateReached(fromDate) ? DateProviderService.getDate().toISOString() : fromDate.toISOString(),
            to: DateProviderService.addHours(minDate, this.betslipUpsellConfig.timeRangeDifferenceValue).toISOString(),
        };
    }
}
